import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { CurrencyMaskModule } from 'ng2-currency-mask';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  DialogViewReportItemMediaComponent,
  MediaTab,
  ReportItemAudioData,
} from '@app-components/dialog/dialog-view-report-item-media/dialog-view-report-item-media.component';
import { StringUtils } from '@app-utils/StringUtils';

/** Dados do item de relatório na visualização (classification vem como 'GOOD' | 'REGULAR' | 'BAD' da API). */
export interface ReportItemViewData {
  name: string;
  classification?: string;
  description?: string;
  fixingCost?: number;
  photosAndVideosUrls?: string[];
  audio?: ReportItemAudioData | null;
}

@Component({
  selector: 'app-report-item-view',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatFormFieldModule, MatInputModule, CurrencyMaskModule, FormsModule],
  templateUrl: './report-item-view.component.html',
  styleUrls: ['./report-item-view.component.scss'],
  animations: [
    trigger('expandCollapse', [
      state('collapsed', style({ height: '0px', overflow: 'hidden', paddingTop: '0px' })),
      state('expanded', style({ height: '*', overflow: 'hidden', paddingTop: '30px' })),
      transition('collapsed => expanded', [
        animate('0.3s ease-out', style({ height: '*', paddingTop: '30px' })),
        animate('0.1s', style({ paddingTop: '30px' })),
      ]),
      transition('expanded => collapsed', [
        animate('0.3s ease-out', style({ height: '0px', paddingTop: '0px' })),
      ]),
    ]),
  ],
})
export class ReportItemViewComponent {
  private readonly dialog = inject(MatDialog);

  item = input.required<ReportItemViewData>();
  titleColor = input<string>('var(--button-branded4)');
  isEditing = input<boolean>(false);

  statusChanged = output<string>();
  descriptionChanged = output<string>();
  costChanged = output<number | null>();

  readonly opened = signal(false);
  readonly imgQty = signal(0);
  readonly audioQty = signal(0);
  readonly videoQty = signal(0);

  readonly mediaTotal = computed(() => this.imgQty() + this.videoQty() + this.audioQty());
  readonly hasMedia = computed(() => this.mediaTotal() > 0);

  constructor() {
    effect(() => {
      const it = this.item();
      if (!it) return;
      const urls = it.photosAndVideosUrls ?? [];
      this.videoQty.set(urls.filter((url: string) => StringUtils.isVideoUrl(url)).length);
      this.imgQty.set(urls.filter((url: string) => !StringUtils.isVideoUrl(url)).length);
      this.audioQty.set(it.audio ? 1 : 0);
    }, { allowSignalWrites: true });
  }

  openMediaViewer(event: Event, initialTab?: MediaTab): void {
    event.stopPropagation();
    if (!this.hasMedia()) return;

    const preferredTab = initialTab ?? this.resolvePreferredTab();

    this.dialog.open(DialogViewReportItemMediaComponent, {
      disableClose: false,
      width: '560px',
      maxWidth: '95vw',
      hasBackdrop: true,
      data: {
        title: this.item().name,
        photosAndVideosUrls: this.item().photosAndVideosUrls ?? [],
        audio: this.item().audio ?? null,
        initialTab: preferredTab,
      },
      panelClass: 'dialog-view-report-item-media',
    });
  }

  toggle(): void {
    this.opened.update(v => !v);
  }

  close(): void {
    this.opened.set(false);
  }

  private resolvePreferredTab(): MediaTab {
    if (this.imgQty() > 0) return 'images';
    if (this.videoQty() > 0) return 'video';
    if (this.audioQty() > 0) return 'audio';
    return 'images';
  }
}
